import React, { FC, useContext, useEffect } from "react";
import classNames from "classnames";
import { useIntl, defineMessages } from "react-intl";

import { useDialog } from "@natera/platform/lib/hooks";
import { CredentialsIdentityProvider } from "@natera/platform/lib/service/session";
import {
  ServiceContext,
  UppAuthContext,
  UserContext,
  NotificationContext,
} from "@app/provider";
import { ResultCodes } from "@app/service/resultCodes";
import AcknowledgementsDialog from "../acknowledgmentsDialog";
import "./signInWithAuthenticationProviders.scss";
import { PatientPortalUser } from "@app/service/session";
import { IDP_TYPE } from "@app/service/user";
import { HEAP_EVENTS } from "@app/provider/types";
import { LinkSource } from "@app/hooks/useTokenizedLinks";
import { Link } from "react-router-dom";
import { routes } from "@app/routing";
import { ACKNOWLEDGMENT_FLOW } from "../acknowledgmentsDialog/acknowledgmentsDialog";
import { LoginStatus } from "@app/provider/user";
import { AuthenticationButton } from "./authenticationButton/AuthenticationButton";
import { sendTokenizedOrOrganicHeapEvent } from "@app/utils";
import { usePortalRedirects } from "@app/hooks";

const messages = defineMessages({
  highlightedTooltipDescriptionGoogle: {
    id: "highlightedTooltipDescriptionGoogle",
    defaultMessage: "You previously logged in with your Google account.",
  },
  highlightedTooltipDescriptionApple: {
    id: "highlightedTooltipDescriptionApple",
    defaultMessage: "You previously logged in with your Apple account.",
  },
  signInWithAuthenticationProvidersUserExistedNotification: {
    id: "signInWithAuthenticationProvidersUserExistedNotification",
    defaultMessage: "This email address is already in use.",
  },
  signInWithAuthenticationProvidersSignInTitle: {
    id: "signInWithAuthenticationProvidersSignInTitle",
    defaultMessage: "Log In",
  },
  signInWithAuthenticationProvidersUnrecognizedError: {
    id: "signInWithAuthenticationProvidersUnrecognizedError",
    defaultMessage: "We're sorry. Something went wrong.",
  },
});

interface SignInWithIdentityProvidersProps {
  className?: string;
  invite: string | null;
  tokenSource?: LinkSource;
  isSignInFlow?: boolean;
}

const SignInWithIdentityProviders: FC<SignInWithIdentityProvidersProps> = ({
  className,
  invite,
  tokenSource,
  isSignInFlow,
}) => {
  const intl = useIntl();

  const { clear, addNotification } = useContext(NotificationContext);
  const { sessionService } = useContext(ServiceContext);
  const {
    startSignInByAuthenticationProvider,
    endSignInByAuthenticationProvider,
    logout,
    signInByAuthenticationProviderInProgress,
  } = useContext(UppAuthContext);
  const {
    uppUserError,
    uppUser,
    deletedUserData,
    loadProfile,
    createUser,
    createUserError,
    resetCreateUserError,
    logUserLogin,
    getUserIdpType,
  } = useContext(UserContext);

  const acknowdledgementsDialog = useDialog(AcknowledgementsDialog);

  const { toLoginRedirect } = usePortalRedirects();

  const handleAgreeAcknowledgements =
    (currentUser: PatientPortalUser) => async () => {
      try {
        await createUser({
          oktaUID: currentUser.uid,
          email: currentUser.sub,
          invite,
        });

        await loadProfile();
      } finally {
        acknowdledgementsDialog.close();
        endSignInByAuthenticationProvider();
      }
    };

  const handleCloseAcknowledgementsDialog = async () => {
    await logout();
    acknowdledgementsDialog.close();
    endSignInByAuthenticationProvider();
  };

  useEffect(() => {
    if (uppUserError) {
      sessionService.getUserData().then((value) => {
        if (
          uppUserError.find(
            (e) => e.code === ResultCodes.USER_NOT_FOUND_ERROR
          ) &&
          value
        ) {
          acknowdledgementsDialog.open({
            userEmail: value.email,
            onAgreeAcknowledgements: handleAgreeAcknowledgements(value),
            onCloseDialog: () => handleCloseAcknowledgementsDialog(),
            flow: ACKNOWLEDGMENT_FLOW.ACCOUNT,
          });
        }
      });
    }

    if (createUserError) {
      switch (createUserError) {
        case ResultCodes.USER_ALREADY_CREATED_ERROR: {
          addNotification({
            title: intl.formatMessage(
              messages.signInWithAuthenticationProvidersUserExistedNotification
            ),
            message: (
              <Link to={routes.logIn}>
                {intl.formatMessage(
                  messages.signInWithAuthenticationProvidersSignInTitle
                )}
              </Link>
            ),
            type: "error",
          });

          break;
        }

        default: {
          addNotification({
            type: "error",
            message: intl.formatMessage(
              messages.signInWithAuthenticationProvidersUnrecognizedError
            ),
          });
        }
      }
      resetCreateUserError();
    }
  }, [uppUserError, createUserError]);

  useEffect(() => {
    const processSignInSuccess = async () => {
      if (uppUser) {
        endSignInByAuthenticationProvider();

        if (signInByAuthenticationProviderInProgress) {
          await logUserLogin({
            email: uppUser.email,
            status: LoginStatus.SUCCESS,
            token: invite,
            idpType: uppUser.idpType,
          });
        }
      }
    };

    processSignInSuccess();
  }, [uppUser]);

  useEffect(() => {
    if (deletedUserData?.success) {
      window.location.reload();
    }
  }, []);

  const handleAuth = async (
    credentials: CredentialsIdentityProvider,
    idp: IDP_TYPE
  ) => {
    clear();
    try {
      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }

      startSignInByAuthenticationProvider();
      await sessionService.loginIdentityProvider(credentials);
      const expectedIdp = await getUserIdpType();
      if (expectedIdp && expectedIdp !== idp) {
        endSignInByAuthenticationProvider();
        const oktaUser = await sessionService.getUserData();
        const idpMismatchEmail = oktaUser?.email?.trim();
        toLoginRedirect({
          params: {
            provider: expectedIdp,
            ...(idpMismatchEmail ? { email: idpMismatchEmail } : {}),
            ...(invite ? { token: invite } : {}),
          },
          preserveQuery: false,
        });
        return;
      } else {
        await loadProfile();
      }
    } catch (error) {
      await sendTokenizedOrOrganicHeapEvent({
        invite,
        tokenizedEventName: HEAP_EVENTS.upp_tokenizedlogin_failure,
        organicEventName: HEAP_EVENTS.upp_organiclogin_failure,
        method: idp,
        source: tokenSource,
      });
      console.error(error);
    }
  };

  return (
    <section
      className={classNames("authentication-providers__container", className)}
    >
      {acknowdledgementsDialog.getDialog()}
      <AuthenticationButton onClick={handleAuth} idp={IDP_TYPE.GOOGLE} />
      <div className="whitespace" />
      <AuthenticationButton onClick={handleAuth} idp={IDP_TYPE.APPLE} />
    </section>
  );
};

export default SignInWithIdentityProviders;
